import {
  activateDrawer,
  lockScroll,
  unlockScroll,
  forceUnlockScrollIfIdle,
  bindDrawerEscape,
  afterDrawerClose,
} from '@theme/coco-utilities';
import { updateShippingProgressAfterUpdate } from '@theme/cart-shipping-progress';

/**
 * Coco cart drawer custom elements: coco-cart-drawer, coco-cart-addon.
 * Public API for ATC / coco-cart-list: refreshFromResponse, getSectionTargets,
 * setActiveElement, setEmptyState, updateCartCount, syncEmptyStateFromHtml, open, close.
 */

const CE_DRAWER = 'coco-cart-drawer';
const CE_ADDON = 'coco-cart-addon';
const addonCloseTimers = new WeakMap();

/** @type {((focusOverride?: HTMLElement | false | null) => void) | null} */
let releaseAddonModal = null;
/** @type {HTMLElement | null} */
let addonTrigger = null;

function finishAddonModalClose(modal) {
  const timer = addonCloseTimers.get(modal);
  if (timer) {
    clearTimeout(timer);
    addonCloseTimers.delete(modal);
  }

  modal.removeAttribute('open');
  modal.setAttribute('hidden', '');
  modal.setAttribute('inert', '');
}

function closeAddonModal(modal) {
  if (!modal.hasAttribute('active') && !modal.hasAttribute('open')) return;

  modal.removeAttribute('active');

  const existingTimer = addonCloseTimers.get(modal);
  if (existingTimer) {
    clearTimeout(existingTimer);
    addonCloseTimers.delete(modal);
  }

  afterDrawerClose(modal, () => finishAddonModalClose(modal));
}

/**
 * @param {{ restoreFocus?: boolean }} [options]
 */
function closeAllAddonModals({ restoreFocus = true } = {}) {
  const trigger = addonTrigger?.isConnected ? addonTrigger : null;

  document.querySelectorAll('[data-cart-addons-drawer][active], [data-cart-addons-drawer][open]').forEach((modal) => {
    closeAddonModal(modal);
  });

  document.querySelectorAll(`[is="${CE_ADDON}"]`).forEach((button) => {
    button.classList.remove('active');
    button.setAttribute('aria-expanded', 'false');
  });

  if (releaseAddonModal) {
    releaseAddonModal(restoreFocus ? trigger : false);
    releaseAddonModal = null;
  }
  addonTrigger = null;
}

/**
 * @param {HTMLElement} modal
 * @param {HTMLElement | null} [triggeredBy]
 */
function openAddonModal(modal, triggeredBy) {
  const timer = addonCloseTimers.get(modal);
  if (timer) {
    clearTimeout(timer);
    addonCloseTimers.delete(modal);
  }

  modal.removeAttribute('hidden');
  modal.removeAttribute('inert');
  modal.setAttribute('open', '');

  addonTrigger = triggeredBy || null;

  requestAnimationFrame(() => {
    modal.setAttribute('active', '');

    releaseAddonModal?.();
    releaseAddonModal = activateDrawer(modal, {
      initialFocus: (drawer) =>
        drawer.querySelector(
          'textarea, input:not([type="hidden"]), select, [is="close-drawer"]:not([data-fixed-overlay])',
        ),
    });
  });
}

function getActiveAddonModal() {
  return document.querySelector('[data-cart-addons-drawer][active]');
}

let cartLinkDelegationBound = false;

function bindCartLinkDelegation() {
  if (cartLinkDelegationBound) return;
  cartLinkDelegationBound = true;

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('[data-cart-link]');
    if (!cartLink) return;

    const drawer = document.querySelector(CE_DRAWER);
    if (!drawer) return;

    event.preventDefault();
    drawer.open(cartLink);
  });
}

class CocoCartDrawer extends HTMLElement {
  #activeElement = null;
  #openModalName = null;
  #releaseModal = null;
  #unbindEscape = null;
  #onClick = null;

  connectedCallback() {
    this.#onClick = (event) => this.#handleClick(event);

    this.#unbindEscape = bindDrawerEscape(this, () => {
      if (getActiveAddonModal()) {
        closeAllAddonModals();
        return;
      }

      if (this.#openModalName) {
        this.closeAllModals();
        return;
      }

      this.close();
    });
    this.addEventListener('click', this.#onClick);
    bindCartLinkDelegation();
    this.#syncCartLinkAria();
  }

  disconnectedCallback() {
    this.#unbindEscape?.();
    this.#unbindEscape = null;
    this.removeEventListener('click', this.#onClick);

    this.#releaseModal?.();
    this.#releaseModal = null;
    closeAllAddonModals({ restoreFocus: false });

    // Theme editor reloads replace this node while open — clear Lenis / body lock.
    this.removeAttribute('active');
    this.removeAttribute('open');
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    forceUnlockScrollIfIdle();
  }

  #syncCartLinkAria() {
    document.querySelectorAll('[data-cart-link]').forEach((cartLink) => {
      cartLink.setAttribute('aria-controls', 'coco-cart-drawer');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      if (!cartLink.hasAttribute('aria-expanded')) {
        cartLink.setAttribute('aria-expanded', 'false');
      }
    });
  }

  #handleClick(event) {
    if (!this.hasAttribute('active')) return;

    if (event.target.closest(`[is="${CE_ADDON}"]`)) return;
    if (event.target.closest('.cart-addons-drawer')) return;

    if (event.target.closest('[data-cart-drawer-modal-close]')) {
      event.preventDefault();
      this.closeAllModals();
      return;
    }

    const modalBackdrop = event.target.closest('[data-cart-drawer-modal]');
    if (modalBackdrop && event.target === modalBackdrop) {
      event.preventDefault();
      this.closeAllModals();
      return;
    }

    const inner = this.querySelector('[data-drawer-inner]');
    const isInsidePanel = inner?.contains(event.target);

    if (event.target.closest('[is="close-drawer"]')) {
      if (event.target.closest('.cart-addons-drawer')) return;

      event.preventDefault();
      this.closeAllModals();
      this.close();
      return;
    }

    if (!isInsidePanel) {
      event.preventDefault();
      this.closeAllModals();
      this.close();
    }
  }

  get #modalsRoot() {
    return this.querySelector('[data-cart-drawer-modals]');
  }

  openModal(name) {
    const modalsRoot = this.#modalsRoot;
    if (!modalsRoot || !name) return;

    this.closeAllModals();
    this.#openModalName = name;
    modalsRoot.hidden = false;

    const modal = modalsRoot.querySelector(`[data-cart-drawer-modal="${name}"]`);
    if (!modal) return;

    modal.hidden = false;
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
    });

    const focusTarget =
      modal.querySelector('textarea, input, select, button:not([data-cart-drawer-modal-close])') ||
      modal.querySelector('[data-cart-drawer-modal-close]');
    focusTarget?.focus?.({ preventScroll: true });
  }

  closeAllModals() {
    const modalsRoot = this.#modalsRoot;
    if (!modalsRoot) return;

    modalsRoot.querySelectorAll('[data-cart-drawer-modal]').forEach((modal) => {
      modal.classList.remove('is-open');
      modal.hidden = true;
    });

    modalsRoot.hidden = true;
    this.#openModalName = null;
  }

  get cartItems() {
    return this.querySelector('coco-cart-list');
  }

  getSectionTargets() {
    return this.cartItems?.getSectionTargets?.() ?? [];
  }

  getSectionsUrl() {
    return this.cartItems?.getSectionsUrl?.() ?? window.location.pathname;
  }

  refreshFromResponse(parsedState) {
    if (!parsedState) return;

    this.cartItems?.refreshFromResponse?.(parsedState);

    const itemCount = this.cartItems?.getItemCount?.(parsedState) ?? parsedState.item_count ?? 0;
    this.setEmptyState(itemCount === 0);

    if (!this.hasAttribute('open')) {
      // Keep restore target from setActiveElement (ATC / QV opener).
      // Only fall back to the header cart link when none was stored.
      const trigger =
        (this.#activeElement?.isConnected ? this.#activeElement : null) ||
        document.querySelector('[data-cart-link]');
      requestAnimationFrame(() => this.open(trigger));
    }
  }

  setEmptyState(isEmpty) {
    this.classList.toggle('is-empty', isEmpty);

    const drawerEmpty = this.querySelector('[data-cart-drawer-empty-region]');
    const drawerBody = this.querySelector('[data-cart-drawer-body]');
    const drawerFooter = this.querySelector('[data-cart-drawer-footer], cart-footer');

    if (drawerEmpty) {
      drawerEmpty.classList.toggle('hidden', !isEmpty);
    }

    [drawerBody, drawerFooter].forEach((element) => {
      if (!element) return;
      element.classList.toggle('hidden', isEmpty);
    });
  }

  syncEmptyStateFromHtml(html) {
    const sourceDrawer =
      html.querySelector?.(CE_DRAWER) ||
      html.querySelector?.(`[data-cart-drawer-host] ${CE_DRAWER}`) ||
      html.querySelector?.(`[id^="shopify-section-"] ${CE_DRAWER}`);
    if (!sourceDrawer) return;

    const sourceEmpty = sourceDrawer.querySelector('[data-cart-drawer-empty-region]');
    const targetEmpty = this.querySelector('[data-cart-drawer-empty-region]');

    if (sourceEmpty && targetEmpty) {
      targetEmpty.innerHTML = sourceEmpty.innerHTML;
      targetEmpty.className = sourceEmpty.className;
    }

    this.setEmptyState(sourceDrawer.classList.contains('is-empty'));
  }

  updateCartCount(count) {
    const paddedCount = count < 10 ? `0${count}` : String(count);
    const template =
      window.Coco?.cartStrings?.itemCount ||
      '{{ count }} items in cart';
    const countLabel = String(template)
      .replace(/\{\{\s*count\s*\}\}/g, String(count))
      .replace('[count]', String(count));
    const emptyLabel = window.Coco?.cartStrings?.cartLabel || 'Cart';

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = paddedCount;
      element.setAttribute('aria-hidden', 'true');
      element.removeAttribute('aria-label');

      if (count === 0) {
        element.classList.add('hidden');
        element.setAttribute('hidden', '');
      } else {
        element.classList.remove('hidden');
        element.removeAttribute('hidden');
      }

      const cartButton = element.closest('.cart-drawer-button, [data-cart-link]');
      if (cartButton) {
        cartButton.setAttribute('aria-label', count > 0 ? countLabel : emptyLabel);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('open', '');
    requestAnimationFrame(() => {
      this.setAttribute('active', '');
      updateShippingProgressAfterUpdate(this);
      requestAnimationFrame(() => this.#loadRecommendations());
    });
    lockScroll();

    // Only toggle aria-expanded on controls that actually own this drawer
    // (header cart link) — not on QV / ATC restore targets.
    if (this.#isCartDrawerControl(triggeredBy)) {
      triggeredBy.setAttribute('aria-expanded', 'true');
    }

    this.#releaseModal?.();
    this.#releaseModal = activateDrawer(this, {
      initialFocus: (drawer) =>
        drawer.querySelector('[is="close-drawer"]:not([data-fixed-overlay])'),
    });
  }

  close() {
    if (!this.hasAttribute('active')) return;

    closeAllAddonModals({ restoreFocus: false });
    this.closeAllModals();
    this.removeAttribute('active');

    const trigger = this.#activeElement;
    if (this.#isCartDrawerControl(trigger)) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.#releaseModal?.(trigger);
    this.#releaseModal = null;

    afterDrawerClose(this, () => {
      if (!this.hasAttribute('active')) {
        this.removeAttribute('open');
        this.setAttribute('hidden', '');
        this.setAttribute('aria-hidden', 'true');
        unlockScroll();
      }
    });
  }

  setActiveElement(element) {
    this.#activeElement = element;
  }

  #isCartDrawerControl(element) {
    if (!element?.getAttribute) return false;
    if (element.hasAttribute('data-cart-link')) return true;
    const controls = element.getAttribute('aria-controls');
    return controls === this.id || controls === 'coco-cart-drawer';
  }

  #loadRecommendations() {
    this.querySelectorAll('coco-recs[data-url]').forEach((element) => {
      if (element.querySelector('[data-swiper-slide]')) return;

      element.reloadRecommendations?.() ?? element.loadRecommendations?.();
    });

    document.querySelector('[data-cart-container]')?.querySelectorAll('coco-recs[data-url]').forEach((element) => {
      if (element.querySelector('[data-swiper-slide]')) return;

      element.reloadRecommendations?.() ?? element.loadRecommendations?.();
    });
  }
}

if (!customElements.get(CE_DRAWER)) {
  customElements.define(CE_DRAWER, CocoCartDrawer);
}

function reinitCartRecommendationsSlider(event) {
  const recommendationsRoot =
    event.target.closest('[data-recommendations-region], [data-cart-drawer-recommendations-region], [data-cart-recommendations-region]') ||
    event.target.closest('.cart-recommendations, [data-recommendations]');
  if (!recommendationsRoot) return;

  const componentSlider = recommendationsRoot.querySelector('component-slider');
  if (!componentSlider?.refreshSlider) return;

  componentSlider.refreshSlider();
}

document.addEventListener('recommendations:loaded', reinitCartRecommendationsSlider);

class CocoCartAddonButton extends HTMLButtonElement {
  #onClick = null;

  connectedCallback() {
    this.#onClick = (event) => this.#handleClick(event);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const addon = this.dataset.cartAddon;
    if (!addon) return;

    const modal = document.getElementById(`${addon}-modal`);
    if (!modal) return;

    closeAllAddonModals({ restoreFocus: false });
    openAddonModal(modal, this);

    this.closest('[data-cart-drawer-addons]')
      ?.querySelectorAll(`[is="${CE_ADDON}"]`)
      .forEach((button) => {
        button.classList.toggle('active', button === this);
        button.setAttribute('aria-expanded', button === this ? 'true' : 'false');
      });
  }
}

if (!customElements.get(CE_ADDON)) {
  customElements.define(CE_ADDON, CocoCartAddonButton, { extends: 'button' });
}

document.addEventListener('click', (event) => {
  const modal = event.target.closest('.cart-addons-drawer[active]');
  if (!modal) return;

  const inner = modal.querySelector('[data-drawer-inner]');
  const shouldClose =
    event.target.closest('[is="close-drawer"]') ||
    event.target.closest('.fixed-overlay') ||
    (inner && !inner.contains(event.target));

  if (!shouldClose) return;

  event.preventDefault();
  event.stopPropagation();
  closeAllAddonModals();
});
